import React, { useEffect, useRef, useState, useCallback } from "react";
import { notification } from "antd";
import SocketManager from "../../../SocketManager";
import Config from "../../API/Config";
import { Routes, Route, Navigate, useNavigate, useLocation, Link } from 'react-router-dom';


const SOCKET_SERVER_URL = Config.baseURL;

// Track shown notifications to prevent duplicates (module-level, survives re-renders)
const shownNotifications = new Set();

const LeadNotification = () => {
    const socketManagerRef = useRef(null);
    const audioRef = useRef(null);
    const audioContextRef = useRef(null);
    const navigate = useNavigate();


    // FIX: Use a ref for audioReady so playNotificationSound never needs it as a dep
    const audioReadyRef = useRef(false);

    // FIX: Use a ref for connectionStatus so displayNotification never needs it as a dep.
    // This breaks the chain: connectionStatus state change → callback recreated →
    // useEffect re-runs → socket torn down and recreated → infinite loop.
    const connectionStatusRef = useRef({
        connected: false,
        socketId: null,
        reconnectAttempts: 0,
        lastEvent: null,
        eventCount: 0
    });

    // Separate lightweight state only for the debug panel UI (does not affect socket)
    const [debugStatus, setDebugStatus] = useState({
        connected: false,
        eventCount: 0,
        lastEvent: null
    });
    const [audioReady, setAudioReady] = useState(false);

    // Refs to the latest versions of callbacks so the socket useEffect (which has
    // an empty dep array) can always call the current version without re-running.
    const displayNotificationRef = useRef(null);
    const playNotificationSoundRef = useRef(null);
    const playFallbackBeepRef = useRef(null);
    const createBeepRef = useRef(null);

    // ─── AUDIO INIT ──────────────────────────────────────────────────────────

    const initializeAudio = useCallback(async () => {
        console.log('🔊 Initializing audio...');
        try {
            audioRef.current = new Audio();
            audioRef.current.preload = "auto";
            audioRef.current.volume = 0.7;

            const audioPaths = [
                "/Sound/Notification.mp3",
                "/Sound/notification.mp3",
                "/sound/Notification.mp3",
                "/sound/notification.mp3"
            ];

            let loaded = false;
            for (const path of audioPaths) {
                try {
                    audioRef.current.src = path;
                    await new Promise((resolve, reject) => {
                        const onCanPlay = () => {
                            audioRef.current.removeEventListener('canplaythrough', onCanPlay);
                            audioRef.current.removeEventListener('error', onError);
                            console.log(`✅ Audio loaded: ${path}`);
                            loaded = true;
                            resolve();
                        };
                        const onError = (e) => {
                            audioRef.current.removeEventListener('canplaythrough', onCanPlay);
                            audioRef.current.removeEventListener('error', onError);
                            reject(e);
                        };
                        audioRef.current.addEventListener('canplaythrough', onCanPlay, { once: true });
                        audioRef.current.addEventListener('error', onError, { once: true });
                        audioRef.current.load();
                    });
                    break;
                } catch (e) {
                    console.log(`❌ Failed to load ${path}:`, e.message);
                }
            }

            if (!loaded) {
                console.warn("⚠️ No audio files could be loaded, will use fallback beep");
            }

            const AudioContextClass = window.AudioContext || window.webkitAudioContext;
            if (AudioContextClass) {
                audioContextRef.current = new AudioContextClass();
                console.log('✅ AudioContext created');
            }

            audioReadyRef.current = true;
            setAudioReady(true);
            console.log("✅ Audio system initialized");
        } catch (error) {
            console.error("❌ Audio initialization failed:", error);
            audioReadyRef.current = true;
            setAudioReady(true);
        }
    }, []); // stable — no changing deps

    // ─── AUDIO PLAYBACK ───────────────────────────────────────────────────────

    const createBeep = useCallback(() => {
        try {
            const ctx = audioContextRef.current;
            if (!ctx) return;
            const now = ctx.currentTime;
            const oscillator = ctx.createOscillator();
            const gainNode = ctx.createGain();
            oscillator.connect(gainNode);
            gainNode.connect(ctx.destination);
            oscillator.type = 'sine';
            oscillator.frequency.setValueAtTime(660, now);
            gainNode.gain.setValueAtTime(0.3, now);
            gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.2);
            oscillator.start(now);
            oscillator.stop(now + 0.2);
            console.log("✅ Fallback beep played");
        } catch (error) {
            console.warn("Failed to create beep:", error);
        }
    }, []); // stable — no changing deps

    const playFallbackBeep = useCallback(() => {
        if (!audioContextRef.current) {
            console.warn("⚠️ No audio context available for fallback");
            return;
        }
        try {
            if (audioContextRef.current.state === 'suspended') {
                audioContextRef.current.resume()
                    .then(() => createBeepRef.current && createBeepRef.current())
                    .catch(err => console.warn("Failed to resume AudioContext:", err));
            } else {
                createBeepRef.current && createBeepRef.current();
            }
        } catch (error) {
            console.warn("Fallback beep failed:", error);
        }
    }, []); // stable — no changing deps

    // FIX: reads audioReadyRef instead of audioReady state so this callback
    // never needs to be recreated when audio state changes
    const playNotificationSound = useCallback(() => {
        if (!audioReadyRef.current) {
            console.log("🔊 Audio not ready yet, skipping sound");
            return;
        }
        if (audioRef.current && audioRef.current.src) {
            try {
                const sound = new Audio(audioRef.current.src);
                sound.volume = 0.7;
                sound.play()
                    .then(() => {
                        console.log("✅ Sound played successfully");
                        setTimeout(() => {
                            sound.pause();
                            sound.src = '';
                        }, 2000);
                    })
                    .catch(err => {
                        console.warn("❌ HTML5 audio failed:", err.message);
                        playFallbackBeepRef.current && playFallbackBeepRef.current();
                    });
                return;
            } catch (error) {
                console.warn("❌ HTML5 audio error:", error);
                playFallbackBeepRef.current && playFallbackBeepRef.current();
            }
        } else {
            playFallbackBeepRef.current && playFallbackBeepRef.current();
        }
    }, []); // stable — no changing deps

    // ─── DESKTOP NOTIFICATION ─────────────────────────────────────────────────

    const showDesktopNotification = useCallback((leadData, customerName, vehicleMake, vehicleModel) => {
        if (!('Notification' in window)) return;
        if (Notification.permission === "granted") {
            const vehicle = `${vehicleMake} ${vehicleModel}`.trim() || 'a vehicle';
            try {
                const desktopNotif = new Notification("🎉 New Lead Assigned!", {
                    body: `${customerName} is interested in ${vehicle}`,
                    icon: "/favicon.ico",
                    badge: "/favicon.ico",
                    tag: `lead-${leadData?.lead?._id || leadData?.leadId}`,
                    requireInteraction: false,
                    silent: true
                });
                desktopNotif.onclick = () => {
                    window.focus();
                    desktopNotif.close();
                };
                setTimeout(() => desktopNotif.close(), 10000);
            } catch (error) {
                console.warn("Failed to show desktop notification:", error);
            }
        } else if (Notification.permission === "default") {
            Notification.requestPermission();
        }
    }, []); // stable — no changing deps

    // FIX: reads connectionStatusRef.current instead of connectionStatus state
    // so this callback never needs connectionStatus as a dependency
    const displayNotification = useCallback((leadData) => {
        const leadId = leadData?.lead?._id || leadData?.leadId;

        if (leadId && shownNotifications.has(leadId)) {
            console.log("⚠️ Notification already shown for lead:", leadId);
            return;
        }
        if (leadId) {
            shownNotifications.add(leadId);
            setTimeout(() => shownNotifications.delete(leadId), 60 * 60 * 1000);
        }

        console.log("📢 Displaying notification for:", leadData);

        const customerName = leadData?.lead?.personalInfo?.fullName ||
            leadData?.personalInfo?.fullName ||
            "A premium customer";
        const vehicleMake = leadData?.lead?.vehicleInfo?.make ||
            leadData?.vehicleInfo?.make ||
            "luxury";
        const vehicleModel = leadData?.lead?.vehicleInfo?.model ||
            leadData?.vehicleInfo?.model ||
            "vehicle";
        const customerEmail = leadData?.lead?.personalInfo?.email ||
            leadData?.personalInfo?.email ||
            "N/A";
        const customerPhone = leadData?.lead?.personalInfo?.phoneNumber ||
            leadData?.personalInfo?.phoneNumber ||
            "N/A";

        const funnyPhrases = [
            "Better call them before they change their mind! 📞",
            "This one's hotter than a test drive in July! 🔥",
            "Show me the money! 💰",
            "Your sales spidey-sense should be tingling! 🕷️",
            "Ka-chow! Time to make a deal! ⚡",
            "This lead is fresher than new car smell! ✨",
            "Warning: Extreme commission potential detected! 🚨",
            "They're ready to buy - are you ready to sell? 💪",
            "Your future yacht thanks you in advance! 🛥️",
            "Cha-ching! Another opportunity! 💵"
        ];

        const randomPhrase = funnyPhrases[Math.floor(Math.random() * funnyPhrases.length)];

        // Read from ref — not from state — so this function stays stable
        const isConnected = connectionStatusRef.current.connected;

        notification.open({
            key: leadId || `lead-${Date.now()}`,
            message: `🎉 New Lead Assigned!`,
            description: (
                <div style={{ whiteSpace: 'pre-line' }}>
                    <div style={{ fontSize: '16px', marginBottom: '8px' }}>
                        <strong>{customerName}</strong> is interested in a {vehicleMake} {vehicleModel}!
                    </div>
                    <div style={{ margin: '8px 0', padding: '8px', background: '#f5f5f5', borderRadius: '4px' }}>
                        <em>"{randomPhrase}"</em>
                    </div>
                    <div style={{ marginTop: '8px' }}>
                        📧 <strong>Email:</strong> {customerEmail}<br />
                        📞 <strong>Phone:</strong> {customerPhone}
                    </div>
                    <div style={{ marginTop: '8px', fontSize: '11px', color: '#999' }}>
                        {isConnected ? '🟢 Connected' : '🔴 Disconnected'} •&nbsp;
                        {new Date().toLocaleTimeString()}
                    </div>
                </div>
            ),
            duration: 10,
            onClick: () => {
                console.log("Notification clicked for lead:", leadId);
            },
            style: {
                cursor: 'pointer',
                borderLeft: '5px solid #52c41a',
                minWidth: '400px',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
            }
        });

        showDesktopNotification(leadData, customerName, vehicleMake, vehicleModel);
    }, [showDesktopNotification]); // stable — showDesktopNotification never changes

    // ─── KEEP REFS IN SYNC WITH LATEST CALLBACK VERSIONS ─────────────────────

    useEffect(() => { displayNotificationRef.current = displayNotification; }, [displayNotification]);
    useEffect(() => { playNotificationSoundRef.current = playNotificationSound; }, [playNotificationSound]);
    useEffect(() => { playFallbackBeepRef.current = playFallbackBeep; }, [playFallbackBeep]);
    useEffect(() => { createBeepRef.current = createBeep; }, [createBeep]);

    // ─── SOCKET SETUP ─────────────────────────────────────────────────────────
    // EMPTY dependency array [] means this runs ONCE on mount and cleans up on
    // unmount. It will NEVER restart due to state/callback changes. All callbacks
    // are accessed via refs so they are always current without being dependencies.

    useEffect(() => {
        const userData = JSON.parse(localStorage.getItem("userData") || "null");
        const currentUserId = userData?.id;

        if (!currentUserId) {
            console.error("❌ No user ID found in localStorage");
            localStorage.removeItem('token');
            localStorage.removeItem('userData');
            localStorage.removeItem('sidebarFrom');

            navigate('/', { replace: true });


            return;
        }

        console.log("🚀 Initializing SocketManager for user:", currentUserId);

        // Initialize audio once
        initializeAudio();

        // Create socket once
        socketManagerRef.current = new SocketManager(SOCKET_SERVER_URL);
        const sm = socketManagerRef.current;

        sm.on('socket-connected', (data) => {
            console.log('✅ Socket connected:', data.socketId);
            connectionStatusRef.current = {
                ...connectionStatusRef.current,
                connected: true,
                socketId: data.socketId,
                reconnectAttempts: 0,
                lastEvent: 'connected'
            };
            setDebugStatus(prev => ({ ...prev, connected: true, lastEvent: 'connected' }));
            sm.joinRoom(`agent_${currentUserId}`);
        });

        sm.on('socket-disconnected', (data) => {
            console.log('❌ Socket disconnected:', data.reason);
            connectionStatusRef.current = {
                ...connectionStatusRef.current,
                connected: false,
                lastEvent: 'disconnected'
            };
            setDebugStatus(prev => ({ ...prev, connected: false, lastEvent: 'disconnected' }));
        });

        sm.on('socket-reconnected', (data) => {
            console.log('🔄 Socket reconnected:', data.socketId);
            connectionStatusRef.current = {
                ...connectionStatusRef.current,
                connected: true,
                socketId: data.socketId,
                reconnectAttempts: 0,
                lastEvent: 'reconnected'
            };
            setDebugStatus(prev => ({ ...prev, connected: true, lastEvent: 'reconnected' }));
            sm.joinRoom(`agent_${currentUserId}`);
        });

        sm.on('socket-error', (data) => {
            console.error('🔴 Socket error:', data);
            connectionStatusRef.current = {
                ...connectionStatusRef.current,
                reconnectAttempts: data.attempt || 0,
                lastEvent: 'error'
            };
            setDebugStatus(prev => ({ ...prev, lastEvent: 'error' }));
        });

        sm.on('room-joined', (data) => {
            console.log('✅ Room joined successfully:', data.room);
        });

        sm.on("lead-assigned", (leadData) => {
            console.log('\n🎯 ===== LEAD ASSIGNED EVENT =====');
            console.log('Lead agentId:', leadData?.agentId);
            console.log('Current user ID:', currentUserId);
            console.log('====================================\n');

            const newCount = (connectionStatusRef.current.eventCount || 0) + 1;
            connectionStatusRef.current = {
                ...connectionStatusRef.current,
                lastEvent: 'lead-assigned',
                eventCount: newCount
            };
            setDebugStatus(prev => ({
                ...prev,
                lastEvent: 'lead-assigned',
                eventCount: (prev.eventCount || 0) + 1
            }));

            const leadAgentId = leadData?.agentId || leadData?.agent?._id;
            if (!leadAgentId) {
                console.warn('⚠️ No agentId found in lead data');
                return;
            }

            const isForCurrentUser = String(leadAgentId) === String(currentUserId);
            if (isForCurrentUser) {
                console.log('✅ LEAD IS FOR CURRENT USER - SHOWING NOTIFICATION');
                // Use refs so we always call the latest version of these functions
                displayNotificationRef.current && displayNotificationRef.current(leadData);
                setTimeout(() => {
                    playNotificationSoundRef.current && playNotificationSoundRef.current();
                }, 100);
            }
        });

        sm.on("lead-queued", (leadData) => {
            console.log("📋 Lead queued:", leadData);
            const newCount = (connectionStatusRef.current.eventCount || 0) + 1;
            connectionStatusRef.current = {
                ...connectionStatusRef.current,
                lastEvent: 'lead-queued',
                eventCount: newCount
            };
            setDebugStatus(prev => ({
                ...prev,
                lastEvent: 'lead-queued',
                eventCount: (prev.eventCount || 0) + 1
            }));

            notification.info({
                message: "Lead Queued",
                description: `A lead has been queued for processing.`,
                duration: 5,
                style: { borderLeft: '5px solid #faad14' }
            });
        });

        // Cleanup ONLY on actual component unmount
        return () => {
            console.log('🧹 Cleaning up LeadNotification');
            if (socketManagerRef.current) {
                socketManagerRef.current.disconnect();
                socketManagerRef.current = null;
            }
        };
    }, []); // ← EMPTY: socket created once, lives until unmount

    // ─── NOTIFICATION PERMISSION ──────────────────────────────────────────────

    useEffect(() => {
        if ('Notification' in window && Notification.permission === "default") {
            Notification.requestPermission();
        }
    }, []);

    // ─── UNLOCK AUDIO ON FIRST USER INTERACTION ───────────────────────────────

    const unlockAudio = useCallback(async () => {
        console.log("🔓 Unlocking audio...");
        try {
            if (audioContextRef.current?.state === 'suspended') {
                await audioContextRef.current.resume();
                console.log("✅ AudioContext resumed");
            }
            if (audioRef.current) {
                audioRef.current.volume = 0.01;
                await audioRef.current.play()
                    .then(() => {
                        audioRef.current.pause();
                        audioRef.current.currentTime = 0;
                        audioRef.current.volume = 0.7;
                        console.log("✅ Audio unlocked");
                    })
                    .catch(err => console.warn("Failed to unlock audio:", err));
            }
        } catch (error) {
            console.error("Failed to unlock audio:", error);
        }
    }, []); // stable — no changing deps

    useEffect(() => {
        const events = ['click', 'keydown', 'touchstart'];
        const handleUserInteraction = () => {
            unlockAudio();
            events.forEach(event => document.removeEventListener(event, handleUserInteraction));
        };
        events.forEach(event => {
            document.addEventListener(event, handleUserInteraction, { once: true });
        });
        return () => {
            events.forEach(event => document.removeEventListener(event, handleUserInteraction));
        };
    }, [unlockAudio]);

    // ─── DEBUG PANEL (development only) ──────────────────────────────────────

    // if (process.env.NODE_ENV === 'development') {
    //     return (
    //         <div style={{
    //             position: 'fixed',
    //             bottom: '20px',
    //             right: '20px',
    //             zIndex: 9999,
    //             background: '#fff',
    //             border: `2px solid ${debugStatus.connected ? '#52c41a' : '#ff4d4f'}`,
    //             borderRadius: '8px',
    //             padding: '12px',
    //             fontSize: '12px',
    //             boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    //             maxWidth: '300px',
    //             opacity: 0.8,
    //             pointerEvents: 'none'
    //         }}>
    //             <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
    //                 🔌 Socket Debug
    //             </div>
    //             <div>
    //                 Status: {debugStatus.connected ?
    //                     <span style={{ color: '#52c41a' }}>✅ Connected</span> :
    //                     <span style={{ color: '#ff4d4f' }}>❌ Disconnected</span>
    //                 }
    //             </div>
    //             <div>Events: {debugStatus.eventCount || 0}</div>
    //             <div>Last: {debugStatus.lastEvent || 'None'}</div>
    //             <div>Audio: {audioReady ? '✅' : '⏳'}</div>
    //         </div>
    //     );
    // }

    return null;
};

export default LeadNotification; 